import { MobileNativeContext } from 'providers/withApplicationContext';
import { SearchQueryStringParams } from 'components/search/utils';
import { getAppConfig } from 'selectors/appSelector';
import { getDomain } from 'utils/domainUtils';
import { toNumber } from 'es-toolkit/compat';
import { useActions } from '@tock/shared/utils/connect';
import { useCallback, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePrevious } from '@tock/hooks';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import { useSelector } from 'react-redux';

/**
 * manages data fetching when a modal is shown for menu or booking
 * in search. will fetch the business config, etc, and clear it out when the
 * modals are closed.
 */
export const useSearchDialogManager = () => {
  const { actions } = useActions();
  const location = useLocation();
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  const isMobileNative = useContext(MobileNativeContext).mobileNative;
  const appConfigBusinessDomain = useSelector(getAppConfig)?.business?.domainName;
  const initializeBusiness = useCallback(
    (businessId: number, businessDomain: string) => {
      Promise.all([
        actions.app.fetchConfig(businessDomain),
        actions.calendar.fetchOfferings(undefined, false, businessId),
        actions.calendar.fetchV2(undefined, false, businessId),
      ]).catch(() => {
        actions.calendar.clearCalendarAndOfferings();
        actions.toast.showError(
          'The information about this experience is not currently available.'
        );
      });
    },
    [actions.app, actions.calendar, actions.toast]
  );

  // it's possible for usePrevious to return undefined, which can cause the second
  // useEffect below to call initializeBusiness an extra time, which results in
  // an infinite loop in the React Native app. Falling back to the current params
  // signals "no change in params" to the code below and fixes this.
  const prevQueryStringParams = usePrevious(queryStringParams) ?? queryStringParams;

  useEffect(() => {
    // When running in React Native, this useEffect continuously calls initializeBusiness
    // and fetches the config. Thus, we add an extra check for isMobileNative to place an
    // additional guard and check that the relevant business config hasn't already been
    // fetched / updated in state
    if (
      (!isMobileNative ||
        (isMobileNative && appConfigBusinessDomain !== queryStringParams.businessDomain)) &&
      queryStringParams.experienceId &&
      queryStringParams.businessId &&
      queryStringParams.businessDomain
    ) {
      initializeBusiness(toNumber(queryStringParams.businessId), queryStringParams.businessDomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * if the experience id changes, re-init
   * if the dialog is closed based on the url, then change
   * config back to www and clear calendar
   */
  useEffect(() => {
    if (
      queryStringParams.experienceId &&
      queryStringParams.experienceId !== prevQueryStringParams?.experienceId &&
      queryStringParams.businessDomain &&
      queryStringParams.businessId
    ) {
      initializeBusiness(toNumber(queryStringParams.businessId), queryStringParams.businessDomain);
    }

    if (
      prevQueryStringParams?.showModal &&
      !queryStringParams.showModal &&
      prevQueryStringParams?.experienceId &&
      !queryStringParams.experienceId
    ) {
      actions.app.fetchConfig(getDomain(location.pathname));
      actions.calendar.clearCalendarAndOfferings();
    }
  }, [
    actions.app,
    actions.calendar,
    initializeBusiness,
    location.pathname,
    prevQueryStringParams?.experienceId,
    prevQueryStringParams?.showModal,
    queryStringParams.businessDomain,
    queryStringParams.businessId,
    queryStringParams.experienceId,
    queryStringParams.showModal,
  ]);

  return null;
};
